import { useEffect, useState } from 'react';
import { CustomSelect } from '@/components/form/CustomSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SortableHeader } from '@/components/SortableHeader';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useColumnSort } from '@/lib/tableSort';
import { formatDateTime } from '@/lib/clientFormat';
import { fetchClientEvents, fetchClientEventRoles, type ClientEventRow } from '@/api/events';
import type { ClientsPageMeta } from '@/api/clients';

type SortKey = 'created_at' | 'status' | 'account_name';

/** 1:1 хардкод из старого Orchid EventListLayout — категории событий ЛК не тянутся из справочника, три фиксированных значения. */
const CATEGORY_OPTIONS = [
  { value: '', label: 'Все категории' },
  { value: 'Планы оценок', label: 'Планы оценок' },
  { value: 'Пользователи', label: 'Пользователи' },
  { value: 'Тесты', label: 'Тесты' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все результаты' },
  { value: 'Успешно', label: 'Успешно' },
  { value: 'Ошибка', label: 'Ошибка' },
];

/** Журнал событий ЛК — 1:1 таблица старого Orchid EventListLayout, поиск по компании + сортировка по дате/результату/компании + фильтр по категории и роли (то, что просили; остальные колонки Orchid-фильтров здесь не портированы). */
export function ClientEventsTable() {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const sort = useColumnSort<SortKey>();
  const [rows, setRows] = useState<ClientEventRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  useEffect(() => {
    fetchClientEventRoles()
      .then(setRoles)
      .catch(() => {
        /* фильтр по роли просто не покажет вариантов — не критично для самого списка */
      });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, category, status, role, sort.key, sort.dir]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    fetchClientEvents({
      search: search || undefined,
      category: category || undefined,
      status: status || undefined,
      role: role || undefined,
      sort: sortParam,
      page,
    })
      .then(({ items, meta: pageMeta }) => {
        if (cancelled) return;
        setRows(items);
        setMeta(pageMeta);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить журнал событий.');
      });
    return () => {
      cancelled = true;
    };
  }, [search, category, status, role, sortParam, page]);

  return (
    <div>
      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по наименованию компании" />
        <div className="group-select">
          <CustomSelect value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
        </div>
        <div className="group-select">
          <CustomSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        </div>
        <div className="group-select">
          <CustomSelect
            value={role}
            onChange={setRole}
            options={[{ value: '', label: 'Все роли' }, ...roles.map((r) => ({ value: r, label: r }))]}
          />
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">События не найдены.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="Дата и время" sortKey="created_at" sort={sort} onSort={sort.toggle} />
                  <SortableHeader
                    label="Наименование компании"
                    sortKey="account_name"
                    sort={sort}
                    onSort={sort.toggle}
                  />
                  <th>Категория</th>
                  <th>Событие</th>
                  <th>Объект</th>
                  <SortableHeader label="Результат" sortKey="status" sort={sort} onSort={sort.toggle} />
                  <th>Детали</th>
                  <th>Пользователь</th>
                  <th>ФИО</th>
                  <th>Роль</th>
                  <th>E-mail входа</th>
                  <th>IP-адрес</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="cell-muted tabular">{formatDateTime(row.createdAt)}</td>
                    <td>{row.accountName ?? '–'}</td>
                    <td>{row.category ?? '–'}</td>
                    <td>{row.name ?? '–'}</td>
                    <td>{row.object ?? '–'}</td>
                    <td>
                      <span className={`badge badge--${row.status === 'Успешно' ? 'good' : 'crit'}`}>
                        <span className="badge__dot" />
                        {row.status ?? '–'}
                      </span>
                    </td>
                    <td className="cell-muted">{row.description ?? '–'}</td>
                    <td>{row.email ?? '–'}</td>
                    <td>{row.fio ?? '–'}</td>
                    <td>{row.role ?? '–'}</td>
                    <td>{row.emailLogin ?? '–'}</td>
                    <td>{row.ipAddress ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && meta.lastPage > 1 && (
            <div className="table-footer">
              <span className="table-footer__status">
                Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
              </span>
              <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
